import "../pages/SetUp.css";
import {
  Alert,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Typography,
} from "antd";
import {
  EnvironmentOutlined,
  BgColorsOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../context/AuthContext";
import PlacePicker from "../components/placePicker/PlacePicker";
import EDocSwitchButton from "../components/eDocSwitch/EDocSwitchButton";
import CO2Switch from "../components/co2Switch/CO2Switch";
import ColorPicker from "../components/colorPicker/ColorPicker";
import LogoPicker from "../components/logoPicker/LogoPicker";
import {
  canManageBranding,
} from "../auth/fleetRoles";

export default function SetUp({ themeColors, themeUpdater, logoUpdater }) {
  const [primaryColor, setPrimaryColor] = useState(themeColors?.primary || "#008080");
  const [secondaryColor, setSecondaryColor] = useState(themeColors?.secondary || "#FFA500");
  const { getFleetID, fleetRole } = useContext(AuthContext);
  const fleetId = getFleetID();
  const { t } = useTranslation();

  const allowBranding = canManageBranding(fleetRole);

  useEffect(() => {
    if (themeColors?.primary) {
      setPrimaryColor(themeColors.primary);
    }
  }, [themeColors?.primary]);

  useEffect(() => {
    if (themeColors?.secondary) {
      setSecondaryColor(themeColors.secondary);
    }
  }, [themeColors?.secondary]);

  const handlePrimaryChange = (newColor) => {
    setPrimaryColor(newColor);
    themeUpdater("primary", newColor);
  };

  const handleSecondaryChange = (newColor) => {
    setSecondaryColor(newColor);
    themeUpdater("secondary", newColor);
  };

  const handleLogoChange = (newLogo) => {
    logoUpdater(newLogo);
  };

  if (!fleetId) {
    return (
      <div className="set-box">
        <Card className="card-container" style={{ maxWidth: 980, margin: "0 auto" }}>
          <Typography.Title level={5} style={{ textAlign: "center" }}>
            {t("setup.loading", { defaultValue: "Loading setup..." })}
          </Typography.Title>
        </Card>
      </div>
    );
  }

  return (
    <div className="setup-page">
      <div className="setup-page-inner">
        <Card className="setup-hero" bordered={false}>
          <Space direction="vertical" size={4}>
            <Typography.Title level={3} style={{ margin: 0 }}>
              {t("navbar.setup", { defaultValue: "Setup" })}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t("setup.manageFleetProfile", {
                defaultValue:
                  "Manage your fleet profile, location and preferences.",
              })}
            </Typography.Text>
          </Space>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} xl={16}>
            <Card
              title={
                <Space>
                  <EnvironmentOutlined />
                  <span>{t("setup.location")}</span>
                </Space>
              }
            >
              <Typography.Text type="secondary">
                {t("setup.locationHelp", {
                  defaultValue: "Set your base location to improve map context and routing defaults.",
                })}
              </Typography.Text>
              <div className="setup-section-content">
                <PlacePicker fleetId={fleetId} />
              </div>
            </Card>
          </Col>

          <Col xs={24} xl={8}>
            <Card
              title={
                <Space>
                  <BgColorsOutlined />
                  <span>{t("setup.fleetBranding", { defaultValue: "Fleet branding" })}</span>
                </Space>
              }
            >
              {!allowBranding ? (
                <Alert
                  showIcon
                  type="warning"
                  style={{ marginBottom: 12 }}
                  message={t("setup.onlyOwnerBranding", { defaultValue: "Only OWNER can update branding." })}
                />
              ) : null}
              <div
                className="setup-brand-preview"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                }}
              >
                <Typography.Text style={{ color: "#fff", fontWeight: 600 }}>
                  {t("setup.livePreview", { defaultValue: "Live theme preview" })}
                </Typography.Text>
              </div>

              <div className="setup-section-content">
                <LogoPicker
                  labelKey="setup.selectLogo"
                  onChange={handleLogoChange}
                  fleetId={fleetId}
                  disabled={!allowBranding}
                />
                <Divider style={{ margin: "12px 0" }} />
                <Space size={24} style={{ width: "100%" }} wrap className="setup-color-row">
                  <ColorPicker
                    labelKey="setup.primaryColor"
                    color={primaryColor}
                    onChange={handlePrimaryChange}
                    colorType="primaryColor"
                    fleetId={fleetId}
                    disabled={!allowBranding}
                  />
                  <ColorPicker
                    labelKey="setup.secondaryColor"
                    color={secondaryColor}
                    onChange={handleSecondaryChange}
                    colorType="secondaryColor"
                    fleetId={fleetId}
                    disabled={!allowBranding}
                  />
                </Space>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <GlobalOutlined />
                  <span>{t("setup.fleetOptions")}</span>
                </Space>
              }
            >
              <Space direction="vertical" size={16} style={{ width: "100%" }}>

                <EDocSwitchButton fleetId={fleetId} />
                <CO2Switch fleetId={fleetId} />
              </Space>
            </Card>
          </Col>


        </Row>

      </div>

    </div>
  );
}
